import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { CONTACT } from "../mock";
import { useToast } from "../hooks/use-toast";
import { sendContact } from "../api";
import Seo from "../components/Seo";
import SocialLinks from "../components/SocialLinks";

const Contact = () => {
  const { toast } = useToast();
  const [params] = useSearchParams();
  const isBirthday = params.get("topic") === "birthday";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: isBirthday ? "Kids birthday party request — Child's age: \nPreferred date: \nNumber of guests: \nAnything else: " : "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")), { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast({ title: "Please check your details", description: "Name and a valid email are required." });
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      toast({ title: "Message sent! \ud83e\udd4b", description: "Thanks for reaching out — we'll get back to you shortly." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error("Contact failed", err);
      toast({ title: "Something went wrong", description: "Please try again or call us directly." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24">
      <Seo
        title="Contact — Alameda Brazilian Jiu-Jitsu & Self-Defense Academy"
        description="Contact Alameda BJJ at 2227 Mariner Square Loop, Alameda, CA 94501. Call 341-688-6866 or email info@alamedabjj.com to book your free Brazilian Jiu-Jitsu class."
        path="/contact"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
      />
      <section className="bg-[#F4F5F1] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Contact</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-[#1A1A1A] max-w-3xl leading-tight">
            Come Train With Us
          </h1>
          <p className="mt-5 text-neutral-600 max-w-2xl leading-relaxed">
            Have a question or ready to book your free trial? Reach out — we'd love to welcome you to the mat.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12">
          {/* info */}
          <div className="reveal">
            <div className="space-y-5">
              {[
                { icon: MapPin, t: "Visit the Dojo", d: CONTACT.address },
                { icon: Phone, t: "Call Us", d: CONTACT.phone, href: CONTACT.phoneHref },
                { icon: Mail, t: "Email Us", d: CONTACT.email, href: `mailto:${CONTACT.email}` },
              ].map((it, i) => (
                <div key={i} className="flex gap-4 bg-white rounded-2xl border border-neutral-100 p-6">
                  <div className="h-12 w-12 rounded-xl bg-br-green/10 flex items-center justify-center shrink-0">
                    <it.icon className="text-br-green" size={22} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">{it.t}</p>
                    {it.href ? (
                      <a href={it.href} className="text-neutral-600 hover:text-br-green transition-colors">{it.d}</a>
                    ) : (
                      <p className="text-neutral-600">{it.d}</p>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex gap-4 bg-white rounded-2xl border border-neutral-100 p-6">
                <div className="h-12 w-12 rounded-xl bg-br-green/10 flex items-center justify-center shrink-0">
                  <Clock className="text-br-green" size={22} />
                </div>
                <div>
                  <p className="font-semibold text-[#1A1A1A]">Training Hours</p>
                  <p className="text-neutral-600">Mon–Fri: 6:00 AM & evenings • Sat: mornings</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-neutral-100 p-6">
                <p className="font-semibold text-[#1A1A1A] mb-3">Follow Us</p>
                <SocialLinks dark={false} size={19} />
              </div>
            </div>
            <div className="mt-6 rounded-2xl overflow-hidden border border-neutral-100 h-64">
              <iframe
                title="Alameda BJJ location"
                width="100%"
                height="100%"
                loading="lazy"
                style={{ border: 0 }}
                src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&output=embed`}
              />
            </div>
          </div>

          {/* form */}
          <div className="reveal">
            <div className="bg-white rounded-2xl border border-neutral-100 p-8 sm:p-10 shadow-sm">
              <h2 className="font-display text-2xl font-bold text-[#1A1A1A]">{isBirthday ? "Request a Birthday Party Quote" : "Send a Message"}</h2>
              <p className="text-neutral-600 mt-2 text-sm">We'll reply as soon as we can.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <Input value={form.name} onChange={change("name")} placeholder="Full name" className="h-12 rounded-xl border-neutral-300" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input type="email" value={form.email} onChange={change("email")} placeholder="Email" className="h-12 rounded-xl border-neutral-300" />
                  <Input value={form.phone} onChange={change("phone")} placeholder="Phone (optional)" className="h-12 rounded-xl border-neutral-300" />
                </div>
                <Textarea value={form.message} onChange={change("message")} placeholder="How can we help?" className="min-h-[130px] rounded-xl border-neutral-300" />
                <Button type="submit" disabled={loading} className="w-full h-12 bg-br-green hover:bg-[#00822f] text-white rounded-full text-base font-semibold disabled:opacity-70">
                  <Send size={17} className="mr-2" /> {loading ? "Sending..." : "Send Message"}
                </Button>
              </form>
              <p className="text-xs text-neutral-400 mt-4 text-center">We'll reply within 1–2 business days.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
